import { motion } from "framer-motion";

export default function Team() {
  return (
    <section id="team" className="relative py-24 lg:py-32 border-t border-[#D4AF37]/10" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-[11px] tracking-[0.45em] uppercase text-[#D4AF37] mb-8"
          data-testid="team-overline"
        >
          Leadership Team
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#F5F5F5]"
          data-testid="team-headline"
        >
          The people behind
          <br />
          <span className="italic text-[#D4AF37]">the standard.</span>
        </motion.h2>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-24 lg:mt-32 pt-16 lg:pt-24 border-t border-[#D4AF37]/10 grid lg:grid-cols-12 gap-14 lg:gap-12 items-center" data-testid="founder-section">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.15 }}
          className="lg:col-span-4"
        >
          <div className="relative">
            <div className="absolute -inset-3 border border-[#D4AF37]/25 translate-x-4 translate-y-4" aria-hidden="true" />
            <div className="relative overflow-hidden bg-[#111111]">
              <img
                src="/images/david-poole.jpg"
                alt="David Poole — Founder and Principal of Veritas Development Group"
                loading="lazy"
                className="w-full h-auto object-cover"
                data-testid="founder-photo"
              />
            </div>
          </div>
          <p className="mt-10 text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]" data-testid="founder-photo-caption">
            David Poole — Founder & Principal
          </p>
        </motion.div>

        <div className="lg:col-span-8">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="text-[11px] tracking-[0.45em] uppercase text-[#D4AF37] mb-6"
            data-testid="founder-overline"
          >
            Founder & Principal
          </motion.p>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.08] tracking-tight text-[#F5F5F5]"
            data-testid="founder-name"
          >
            David Poole
          </motion.h3>
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="block w-12 h-px bg-[#D4AF37]/60 mt-6"
            aria-hidden="true"
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-5 font-display text-lg sm:text-xl italic text-[#D4AF37]"
            data-testid="founder-tagline"
          >
            Building Vision. Creating Value. Delivering Results.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-6 max-w-2xl space-y-5 text-base font-light leading-relaxed tracking-wide text-[#A3A3A3]"
            data-testid="founder-bio"
          >
            <p>
              David Poole is the Founder and Principal of Veritas Development
              Group LLC, bringing more than two decades of experience in
              construction, land development, real estate investment, and
              project leadership.
            </p>
            <p>
              Throughout his career, David has led projects from initial land
              acquisition and feasibility through planning, construction, and
              final delivery. His expertise spans residential communities,
              multifamily developments, mixed-use projects, commercial
              construction, and strategic real estate development.
            </p>
            <p>
              Known for combining practical construction knowledge with
              long-term investment strategy, David works alongside investors,
              municipalities, consultants, architects, engineers, and
              contractors to transform opportunities into successful
              developments. His approach is built on disciplined planning,
              financial analysis, risk management, and attention to detail at
              every stage of the development process.
            </p>
            <p>
              Under David's leadership, Veritas Development Group is committed
              to creating projects that strengthen communities while delivering
              lasting value for partners and investors. Every project reflects
              the company's core principles of integrity, excellence,
              transparency, and quality.
            </p>
            <p>
              For David, development is more than building structures — it's
              about creating places where businesses grow, families thrive, and
              communities prosper.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-10 grid grid-cols-2 gap-6 max-w-xl"
            data-testid="founder-stats"
          >
            <div className="border-l border-[#D4AF37]/30 pl-4">
              <p className="font-display text-3xl sm:text-4xl text-[#D4AF37]">22+</p>
              <p className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">Years Construction & Development Experience</p>
            </div>
            <div className="border-l border-[#D4AF37]/30 pl-4">
              <p className="font-display text-3xl sm:text-4xl text-[#D4AF37]">13+</p>
              <p className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">Years Senior Development Leadership</p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="mt-10"
            data-testid="founder-specialties"
          >
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#D4AF37] mb-4">Specialties</p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 max-w-2xl">
              {[
                "Land Acquisition & Development",
                "Residential & Mixed-Use Communities",
                "Multifamily Development",
                "Commercial Real Estate",
                "Project Planning & Feasibility",
                "Construction Management",
                "Investment & Development Strategy",
              ].map((o) => (
                <li key={o} className="flex items-baseline gap-3 text-[13px] tracking-wide text-[#F5F5F5]/90">
                  <span className="w-1.5 h-1.5 bg-[#D4AF37] rotate-45 shrink-0" />
                  {o}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-24 lg:mt-32 pt-16 lg:pt-24 border-t border-[#D4AF37]/10 grid lg:grid-cols-12 gap-14 lg:gap-12 items-center" data-testid="founder2-section">
        <div className="lg:col-span-8 order-2 lg:order-1">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="text-[11px] tracking-[0.45em] uppercase text-[#D4AF37] mb-6"
            data-testid="founder2-overline"
          >
            Construction & Concrete Specialist
          </motion.p>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.08] tracking-tight text-[#F5F5F5]"
            data-testid="founder2-name"
          >
            Mike Poole
          </motion.h3>
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="block w-12 h-px bg-[#D4AF37]/60 mt-6"
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-6 max-w-2xl space-y-5 text-base font-light leading-relaxed tracking-wide text-[#A3A3A3]"
            data-testid="founder2-bio"
          >
            <p>
              Originally from Bellingham, Washington, Mike Poole built his
              career across Vancouver Island's largest cities in residential,
              bridge, and commercial concrete construction. He brings 23 years
              of hands-on industry experience: thirteen years in residential
              construction, specializing in home building and foundations,
              before transitioning into bridge construction and large-scale
              commercial concrete projects.
            </p>
            <p>
              Highly skilled at reading and interpreting large-scale
              construction plans, Mike specializes in concrete construction,
              foundations, and structural concrete — coordinating trades,
              leading field operations, and holding every pour to demanding
              quality standards. With decades of leadership and hands-on
              technical experience, he is known for delivering durable,
              high-quality work across a wide range of construction
              environments.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-10 grid grid-cols-2 gap-6 max-w-md"
            data-testid="founder2-stats"
          >
            <div className="border-l border-[#D4AF37]/30 pl-4">
              <p className="font-display text-3xl sm:text-4xl text-[#D4AF37]">23</p>
              <p className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">Years Hands-On Experience</p>
            </div>
            <div className="border-l border-[#D4AF37]/30 pl-4">
              <p className="font-display text-3xl sm:text-4xl text-[#D4AF37]">38</p>
              <p className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">Years on Vancouver Island</p>
            </div>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="mt-8 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80"
            data-testid="founder2-roles"
          >
            Concrete Construction &nbsp;·&nbsp; Plan Interpretation &nbsp;·&nbsp; Field Operations &nbsp;·&nbsp; Quality Control
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.15 }}
          className="lg:col-span-4 relative order-1 lg:order-2"
        >
          <div className="relative">
            <div className="absolute -inset-3 border border-[#D4AF37]/25 -translate-x-4 translate-y-4" aria-hidden="true" />
            <div className="relative overflow-hidden bg-[#111111]">
              <img
                src="/images/mike-poole.jpg"
                alt="Mike Poole — Construction and Concrete Specialist at Veritas Development Group"
                loading="lazy"
                className="w-full h-auto object-cover"
                data-testid="founder2-photo"
              />
            </div>
          </div>
          <p className="mt-10 text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]" data-testid="founder2-photo-caption">
            Mike Poole — Construction & Concrete Specialist
          </p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-24 lg:mt-32 pt-16 lg:pt-24 border-t border-[#D4AF37]/10 grid lg:grid-cols-12 gap-14 lg:gap-12 items-center" data-testid="founder3-section">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.15 }}
          className="lg:col-span-4"
        >
          <div className="relative">
            <div className="absolute -inset-3 border border-[#D4AF37]/25 translate-x-4 translate-y-4" aria-hidden="true" />
            <div className="relative overflow-hidden bg-[#111111]">
              <img
                src="/images/daniel-bailey.jpg"
                alt="Daniel Bailey — Real Estate Advisor and Development Consultant at Veritas Development Group"
                loading="lazy"
                className="w-full h-auto object-cover"
                data-testid="founder3-photo"
              />
            </div>
          </div>
          <p className="mt-10 text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]" data-testid="founder3-photo-caption">
            Daniel Bailey — Co-Founder & Real Estate Advisor
          </p>
        </motion.div>

        <div className="lg:col-span-8">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="text-[11px] tracking-[0.45em] uppercase text-[#D4AF37] mb-6"
            data-testid="founder3-overline"
          >
            Co-Founder | Real Estate &amp; Development Advisory — Lee's Summit, Missouri
          </motion.p>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.08] tracking-tight text-[#F5F5F5]"
            data-testid="founder3-name"
          >
            Daniel Bailey
          </motion.h3>
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="block w-12 h-px bg-[#D4AF37]/60 mt-6"
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-6 max-w-2xl space-y-5 text-base font-light leading-relaxed tracking-wide text-[#A3A3A3]"
            data-testid="founder3-bio"
          >
            <p>
              Daniel Bailey is a Co-Founder and Real Estate Advisor with
              Veritas Development Group, rooted in Lee's Summit, Missouri.
              Born and raised in the community, he has built a trusted
              reputation through integrity, hard work, and consistent,
              results-driven guidance. With extensive experience in the
              Missouri real estate market, Daniel represents buyers, sellers,
              investors, and developers across Lee's Summit, Kansas City, and
              the surrounding communities — bringing clarity, confidence, and
              strong direction to every transaction.
            </p>
            <p>
              Daniel's career foundation with Keller Williams has given him a
              comprehensive understanding of the full real estate process, from
              identifying opportunities and evaluating risk to negotiating
              contracts, coordinating details, and closing with confidence.
              Beyond traditional brokerage support, he brings hands-on
              expertise in investing, land acquisition, development consulting,
              and project planning, helping clients pursue strategies that
              create long-term value.
            </p>
            <p>
              Clients appreciate Daniel's straightforward communication,
              disciplined negotiation, and unwavering commitment to protecting
              their interests at every step. His local expertise, professional
              network, and passion for real estate consistently open doors —
              and close deals where others can't.
            </p>
            <p>
              Daniel actively practices real estate with Keller Williams; his
              brokerage activities are performed through Keller Williams,
              separate from his development and advisory work with Veritas
              Development Group.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-2xl"
            data-testid="founder3-stats"
          >
            <div className="border-l border-[#D4AF37]/30 pl-4">
              <p className="font-display text-2xl sm:text-3xl text-[#D4AF37]">Lee's Summit</p>
              <p className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">Born & Raised</p>
            </div>
            <div className="border-l border-[#D4AF37]/30 pl-4">
              <p className="font-display text-2xl sm:text-3xl text-[#D4AF37]">Keller Williams</p>
              <p className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">Career Foundation</p>
            </div>
            <div className="border-l border-[#D4AF37]/30 pl-4">
              <p className="font-display text-2xl sm:text-3xl text-[#D4AF37]">9 Years</p>
              <p className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">Professional Real Estate Guidance, Investing & Leading Clients</p>
            </div>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="mt-8 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80"
            data-testid="founder3-roles"
          >
            Co-Founder &nbsp;·&nbsp; Advisor &nbsp;·&nbsp; Investor &nbsp;·&nbsp; Development Consultant
          </motion.p>
        </div>
      </div>
    </section>
  );
}
